import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { getCustomerData } from '../../api/art';
import { ContactInfo } from '../../types/wizard';

interface ContactDataProps {
  currentContact?: ContactInfo;
  onContactChange: (contact: ContactInfo) => void;
}

export const ContactData: React.FC<ContactDataProps> = ({
  currentContact,
  onContactChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState<ContactInfo>(
    currentContact || { email: '', fullName: '', phone: '' }
  );
  const [defaultContact, setDefaultContact] = useState<ContactInfo>({
    email: '',
    fullName: '',
    phone: ''
  });
  const [isLoadingDefault, setIsLoadingDefault] = useState(true);

  // Fetch default customer data
  useEffect(() => {
    const fetchDefaultData = async () => {
      const customerId = localStorage.getItem("customerId");
      if (!customerId) {
        setIsLoadingDefault(false);
        return;
      }

      try {
        const result = await getCustomerData(customerId);
        if (result.success && result.data) {
          setDefaultContact(result.data);
        }
      } catch (error) {
        console.error("Error loading default customer data:", error);
      } finally {
        setIsLoadingDefault(false);
      }
    };

    fetchDefaultData();
  }, []);

  useEffect(() => {
    if (currentContact) {
      setFormData(currentContact);
    }
  }, [currentContact]);

  const isCustomContact = formData.email || formData.fullName || formData.phone;
  const displayContact = {
    email: formData.email || defaultContact.email || '',
    fullName: formData.fullName || defaultContact.fullName || '',
    phone: formData.phone || defaultContact.phone || ''
  };

  const isUsingDefaultEmail = !formData.email && defaultContact.email;
  const isUsingDefaultName = !formData.fullName && defaultContact.fullName;
  const isUsingDefaultPhone = !formData.phone && defaultContact.phone;

  const handleInputChange = (field: keyof ContactInfo, value: string) => {
    const updatedContact = { ...formData, [field]: value };
    setFormData(updatedContact);
    onContactChange(updatedContact);
  };

  if (isLoadingDefault) {
    return (
      <div className="py-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold">Contact Data</span>
          <span className="text-xs">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left p-0 outline-none hover:outline-none focus:outline-none"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold mb-3">Contact Data</span>
            {isCustomContact && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 mb-2">
                Custom
              </span>
            )}
          </div>
          <div className="text-sm font-medium">
            {displayContact.fullName || 'Not specified'}
          </div>
          <div className="text-xs text-gray-600">
            {displayContact.email || 'No email provided'}
          </div>
          {displayContact.phone && (
            <div className="text-xs text-gray-600">
              {displayContact.phone}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-orange-600 font-medium">
            {isExpanded ? 'Close' : 'Edit'}
          </span>
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="w-3 h-3 text-gray-400"
          />
        </div>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3 pb-4">
          <div className="bg-[#FFF4E5] border border-orange-200 rounded-md p-3">
            <p className="text-xs text-[#663c00]">
              <strong>Note:</strong> Leave any field blank to use defaults
            </p>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="contact-email" className="block text-xs font-medium mb-1">
              Notifications Email:
            </label>
            <input
              id="contact-email"
              type="email"
              className="p-2.5 w-full border-b-2 rounded outline-none"
              style={{ border: '1px solid rgb(204, 204, 204)' }}
              placeholder="Enter notifications email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
            {isUsingDefaultEmail && (
              <div className="mt-2 text-xs">
                Default: <span className='text-[#F36E29] font-medium'>{defaultContact.email}</span>
              </div>
            )}
          </div>

          {/* Contact Name */}
          <div>
            <label htmlFor="contact-name" className="block text-xs font-medium mb-1">
              Contact Name:
            </label>
            <input
              id="contact-name"
              type="text"
              className="p-2.5 w-full border-b-2 rounded outline-none"
              style={{ border: '1px solid rgb(204, 204, 204)' }}
              placeholder="Enter contact name"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
            />
            {isUsingDefaultName && (
              <div className="mt-2 text-xs">
                Default: <span className='text-[#F36E29] font-medium'>{defaultContact.fullName}</span>
              </div>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="contact-phone" className="block text-xs font-medium mb-1">
              Contact Phone:
            </label>
            <input
              id="contact-phone"
              type="tel"
              className="p-2.5 w-full border-b-2 rounded outline-none"
              style={{ border: '1px solid rgb(204, 204, 204)' }}
              placeholder="Enter contact phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />
            {isUsingDefaultPhone && (
              <div className="mt-2 text-xs">
                Default: <span className='text-[#F36E29] font-medium'>{defaultContact.phone}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};